import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Api as ApiIcon,
  CloudUpload as CloudUploadIcon,
  // Image as ImageIcon,
  Delete as DeleteIcon,
  AddCircleOutlineOutlined,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import type { EndpointRequest, HttpMethod, Priority } from "../types";

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      detail?: string;
      [key: string]: unknown;
    };
  };
}

interface CreateRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: Omit<EndpointRequest, "id">) => void;
}

const HTTP_METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const PRIORITIES: Priority[] = ["low", "medium", "high", "urgent"];

interface Component {
  id: number;
  name: string;
  description?: string;
}

export const CreateRequestDialog: React.FC<CreateRequestDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [components, setComponents] = useState<Component[]>([]);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<Omit<EndpointRequest, "id">>({
    component: "",
    task: "",
    lead: "",
    method: "GET",
    status: "pending",
    priority: "medium",
    requestDate: new Date().toISOString().split("T")[0],
    targetDeliveryDate: "",
    expectedDeliveryDate: "",
    challenges: "",
    description: "",
    payload: "",
  });

  const [requestDate, setRequestDate] = useState<Dayjs | null>(dayjs());
  const [targetDate, setTargetDate] = useState<Dayjs | null>(null);
  const [expectedDate, setExpectedDate] = useState<Dayjs | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [, setImageFile] = useState<File | null>(null);
  const [openAddComponentDialog, setOpenAddComponentDialog] = useState(false);
  const [newComponentData, setNewComponentData] = useState<{
    name: string;
    description: string;
  }>({
    name: "",
    description: "",
  });
  const [savingNewComponent, setSavingNewComponent] = useState(false);

  // Fetch components when dialog opens
  useEffect(() => {
    if (open) {
      fetchComponents();
      // Auto-populate lead with username
      if (user?.username) {
        setFormData((prev) => ({ ...prev, lead: user.username }));
      }
    }
  }, [open, user]);

  const fetchComponents = async () => {
    setLoadingComponents(true);
    try {
      const response = await apiService.get<{ results?: Component[] } | Component[]>(
        "/api/endpoint-request/components/"
      );
      // Handle both array and paginated response formats
      const componentsData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];
      setComponents(componentsData);
    } catch (error: unknown) {
      console.error("Failed to fetch components:", error);
      toast.error("Failed to load components. Please try again.");
    } finally {
      setLoadingComponents(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImagePreview(result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
  };

  const handleOpenAddComponentDialog = () => {
    setOpenAddComponentDialog(true);
    setNewComponentData({ name: "", description: "" });
  };

  const handleCloseAddComponentDialog = () => {
    setOpenAddComponentDialog(false);
    setNewComponentData({ name: "", description: "" });
  };

  const handleNewComponentChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setNewComponentData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveNewComponent = async () => {
    if (!newComponentData.name.trim()) {
      toast.error("Component name is required");
      return;
    }

    setSavingNewComponent(true);
    try {
      const response = await apiService.post<Component>(
        "/api/endpoint-request/components/",
        {
          name: newComponentData.name.trim(),
          description: newComponentData.description.trim() || "",
        }
      );
      const savedComponent = response.data;
      toast.success("Component added successfully!");

      // Add the new component to the list
      setComponents((prev) => [...prev, savedComponent]);

      // Select the newly added component
      setFormData((prev) => ({
        ...prev,
        component: savedComponent.id.toString(),
      }));

      handleCloseAddComponentDialog();
    } catch (error: unknown) {
      console.error("Failed to save new component:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        (apiError.response?.data
          ? Object.values(apiError.response.data)
              .flat()
              .map((value) => String(value))
              .join(" ")
          : undefined) ||
        "Failed to add component. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSavingNewComponent(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.component || !formData.task || !formData.lead) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Parse payload JSON if provided
    let parsedPayload: Record<string, unknown> = {};
    if (formData.payload && formData.payload.trim()) {
      try {
        parsedPayload = JSON.parse(formData.payload) as Record<string, unknown>;
      } catch (error) {
         console.error("JSON parse failed:", error);
        toast.error("Invalid JSON in payload field. Please check your JSON syntax.");
        return;
      }
    }

    // Find the selected component ID
    const selectedComponent = components.find(
      (c) => c.id.toString() === formData.component || c.name === formData.component
    );
    if (!selectedComponent) {
      toast.error("Please select a valid component");
      return;
    }

    setSubmitting(true);
    try {
      // Transform form data to match API payload structure
      const apiPayload: Record<string, unknown> = {
        component: selectedComponent.id,
        task_description: formData.task,
        http_method: formData.method || "GET",
        priority: formData.priority || "medium",
        additional_information: formData.description || "",
        endpoint_url: "",
      };

      // Only include payload if it has content
      if (Object.keys(parsedPayload).length > 0) {
        apiPayload.payload = parsedPayload;
      }

      // Add target delivery date if provided (convert to ISO format)
      if (targetDate) {
        apiPayload.target_delivery_date_time = targetDate.toISOString();
      }

      // Submit to API endpoint for creating endpoint requests
      await apiService.post("/api/endpoint-request/endpoint-requests/", apiPayload);

      toast.success("Endpoint request created successfully!");

      // Also call the onSubmit prop for local state management if needed
      const submitData = {
        ...formData,
        requestDate: requestDate?.format("YYYY-MM-DD") || formData.requestDate,
        targetDeliveryDate: targetDate?.format("YYYY-MM-DD") || undefined,
        expectedDeliveryDate: expectedDate?.format("YYYY-MM-DD") || undefined,
      };
      onSubmit(submitData);
      handleClose();
    } catch (error: unknown) {
      console.error("Failed to create endpoint request:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to create endpoint request. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      component: "",
      task: "",
      lead: "",
      method: "GET",
      status: "pending",
      priority: "medium",
      requestDate: new Date().toISOString().split("T")[0],
      targetDeliveryDate: "",
      expectedDeliveryDate: "",
      challenges: "",
      description: "",
      payload: "",
    });
    setRequestDate(dayjs());
    setTargetDate(null);
    setExpectedDate(null);
    setImagePreview(null);
    setImageFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ApiIcon />
          <Typography variant="h6">Create New Endpoint Request</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Component *</InputLabel>
                <Select
                  value={formData.component}
                  label="Component *"
                  onChange={(e) => setFormData({ ...formData, component: e.target.value })}
                  disabled={loadingComponents}
                  endAdornment={
                    loadingComponents ? (
                      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : null
                  }
                >
                  {components.map((component) => (
                    <MenuItem key={component.id} value={component.id.toString()}>
                      {component.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                startIcon={<AddCircleOutlineOutlined />}
                onClick={handleOpenAddComponentDialog}
                sx={{
                  mt: 1,
                  textTransform: "none",
                }}
                size="small"
              >
                Add Component
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Lead *"
                value={formData.lead}
                onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
                placeholder="e.g., Dancel"
                required
                disabled
                
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Task Description *"
                value={formData.task}
                onChange={(e) => setFormData({ ...formData, task: e.target.value })}
                placeholder="Describe the endpoint requirement..."
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>HTTP Method</InputLabel>
                <Select
                  value={formData.method}
                  label="HTTP Method"
                  onChange={(e) =>
                    setFormData({ ...formData, method: e.target.value as HttpMethod })
                  }
                >
                  {HTTP_METHODS.map((method) => (
                    <MenuItem key={method} value={method}>
                      <Chip
                        label={method}
                        size="small"
                        sx={{
                          backgroundColor:
                            method === "GET"
                              ? "#4caf50"
                              : method === "POST"
                              ? "#2196f3"
                              : method === "PUT"
                              ? "#ff9800"
                              : method === "PATCH"
                              ? "#9c27b0"
                              : "#f44336",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value as Priority })
                  }
                >
                  {PRIORITIES.map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      {priority.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <DateTimePicker
                label="Target Delivery Date & Time"
                value={targetDate}
                onChange={(newValue) => setTargetDate(newValue as Dayjs | null)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Payload/Request Body (JSON)"
                value={formData.payload}
                onChange={(e) => setFormData({ ...formData, payload: e.target.value })}
                placeholder='{"key": "value"}'
                multiline
                rows={4}
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Additional Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                placeholder="Any additional context or requirements..."
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  border: "2px dashed",
                  borderColor: "divider",
                  backgroundColor: "background.default",
                }}
              >
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                  Upload Image Showing Where Endpoint Would Be Applied
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Upload a screenshot or image showing where this endpoint will be used in the application.
                  This helps backend developers understand the context better.
                </Alert>
                {imagePreview ? (
                  <Box sx={{ position: "relative", mt: 2 }}>
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Endpoint application preview"
                      sx={{
                        width: "100%",
                        maxHeight: 400,
                        objectFit: "contain",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                    <IconButton
                      onClick={handleRemoveImage}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "error.main",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "error.dark",
                        },
                      }}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      border: "2px dashed",
                      borderColor: "primary.main",
                      borderRadius: 2,
                      p: 3,
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "primary.dark",
                        backgroundColor: "action.hover",
                      },
                    }}
                    onClick={() => document.getElementById("image-upload-input")?.click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      Click to upload or drag and drop
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PNG, JPG, GIF up to 5MB
                    </Typography>
                    <input
                      id="image-upload-input"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: "none" }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={submitting || loadingComponents}
        >
          {submitting ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Creating...
            </>
          ) : (
            "Create Request"
          )}
        </Button>
      </DialogActions>

      {/* Add Component Dialog */}
      <Dialog
        open={openAddComponentDialog}
        onClose={handleCloseAddComponentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Component</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Component Name *"
            type="text"
            fullWidth
            variant="outlined"
            value={newComponentData.name}
            onChange={handleNewComponentChange}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            margin="dense"
            name="description"
            label="Component Description"
            type="text"
            fullWidth
            variant="outlined"
            value={newComponentData.description}
            onChange={handleNewComponentChange}
            multiline
            rows={4}
            placeholder="Describe what this component is used for..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddComponentDialog} disabled={savingNewComponent}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewComponent}
            variant="contained"
            color="primary"
            disabled={savingNewComponent || !newComponentData.name.trim()}
          >
            {savingNewComponent ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

